import axios from 'axios';

// CRA (react-scripts) exposes env vars prefixed with REACT_APP_ at build time.
// ?? (not ||) so an explicit empty string — set at Docker build time to mean
// "same-origin, let Caddy route /api/* to the backend" — isn't overridden by the fallback.
const baseURL = process.env.REACT_APP_API_URL ?? 'http://localhost:5299';

export const api = axios.create({ baseURL });

// Partner-uploaded photos come back as API-relative paths (/api/images/{id}, /api/offer-images/{id});
// prefix them with the API origin so <img> resolves them in dev too. Anything else in filePath is a
// legacy PHP-era filename that isn't served by this stack, so it's treated as "no image" (callers fall back).
export function venueImageUrl(path?: string | null): string | undefined {
  return path && (path.startsWith('/api/images/') || path.startsWith('/api/offer-images/')) ? `${baseURL}${path}` : undefined;
}

// Android app built by the pipeline (GET /api/app/android). available=false until an APK is deployed.
export interface AndroidAppInfo {
  available: boolean;
  versionCode?: number | null;
  versionName?: string | null;
  builtAtUtc?: string | null;
  sizeBytes?: number | null;
  signed?: boolean | null;
  downloadUrl?: string | null;
}

export const getAndroidAppInfo = async (): Promise<AndroidAppInfo> => (await api.get<AndroidAppInfo>('/api/app/android')).data;

export const androidApkUrl = `${baseURL}/api/app/android/download`;

// When an admin opens a venue from the admin console (/partner-account...?restoId=<id>), every
// /api/account/* call carries that venue's id; the API honours it only for admin tokens. Read from
// the current URL at request time rather than kept in a variable, so it can never go stale between
// a component's render and its cleanup. null = act on the logged-in account.
function actingRestoId(): number | null {
  const path = window.location.pathname;
  if (localStorage.getItem('c2n_is_admin') !== '1' || !(path.startsWith('/partner-account') || path.startsWith('/pos'))) return null;
  return Number(new URLSearchParams(window.location.search).get('restoId')) || null;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('c2n_token');
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  const restoId = actingRestoId();
  if (restoId !== null && (config.url?.startsWith('/api/account/') || config.url?.startsWith('/api/pos/'))) {
    config.params = { ...(config.params ?? {}), restoId };
  }
  return config;
});

export interface LoginResponse {
  accessToken: string;
  email: string;
  expiresAtUtc: string;
  isAdmin: boolean;
}

export async function login(email: string, password: string): Promise<LoginResponse> {
  const { data } = await api.post<LoginResponse>('/api/auth/login', { email, password });
  return data;
}

export interface OwnerRegisterRequest {
  email: string;
  name: string;
  address: string;
  mobile: string;
  regCode: string;
}

export async function registerOwner(request: OwnerRegisterRequest): Promise<{ message: string }> {
  const { data } = await api.post('/api/owner/register', request);
  return data;
}

export interface AccountDetails {
  restoId: number;
  name: string;
  address: string;
  mobile1?: string;
  mobile2?: string;
  offer?: string;
  filePath?: string;
  // Comma-separated recurring weekly schedule, e.g. "Saturday,Sunday".
  activeDays?: string;
  isActive: boolean;
  offerImageUrl?: string;
}

export async function getAccountDetails(): Promise<AccountDetails> {
  const { data } = await api.get<AccountDetails>('/api/account/me');
  return data;
}

export async function updateOffer(offer: string, activeDays: string[]): Promise<{ message: string }> {
  const { data } = await api.post('/api/account/update-offer', { offer, activeDays });
  return data;
}

export const PHOTO_CATEGORIES = ['Bar', 'Ambience', 'Food', 'Exterior', 'Events', 'Other'] as const;
export const OFFER_PHOTO_CATEGORY = 'Offer';
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

export interface VenueImage {
  id: number;
  url: string;
  category: string;
  isCover: boolean;
}

export interface ImageUploadResult {
  success: boolean;
  message: string;
  image?: VenueImage;
}

export async function getMyImages(): Promise<VenueImage[]> {
  const { data } = await api.get<VenueImage[]>('/api/account/images');
  return data;
}

export async function uploadImage(file: File, category: string): Promise<ImageUploadResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('category', category);
  const { data } = await api.post<ImageUploadResult>('/api/account/images', form);
  return data;
}

export async function replaceImage(id: number, file: File): Promise<ImageUploadResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.put<ImageUploadResult>(`/api/account/images/${id}`, form);
  return data;
}

export async function updateImageCategory(id: number, category: string): Promise<ImageUploadResult> {
  const { data } = await api.patch<ImageUploadResult>(`/api/account/images/${id}`, { category });
  return data;
}

export async function deleteImage(id: number): Promise<void> {
  await api.delete(`/api/account/images/${id}`);
}

export async function setCoverImage(id: number): Promise<void> {
  await api.post(`/api/account/images/${id}/cover`);
}

export async function getVenueImages(restoId: number): Promise<VenueImage[]> {
  const { data } = await api.get<VenueImage[]>(`/api/discovery/restaurants/${restoId}/images`);
  return data;
}

// Partner-portal offers. Dates are "yyyy-MM-dd", times "HH:mm:ss".
export const OFFER_TYPES = ['Free Entry / Complimentary', 'Percentage Discount', 'Buy 1 Get 1', 'Flat Discount', 'Happy Hours', 'Other'] as const;
export const MAX_OFFER_IMAGES = 5;
export const MAX_OFFER_DESCRIPTION = 250;
export const MAX_OFFER_TERMS = 500;

// NeedsCategory: published before categories existed, hidden until the partner picks one.
// Pending/Rejected: alcohol-related offers awaiting (or refused) admin approval.
// Suspended: hidden by a Cheers2Night admin. moderationNote holds the admin's reason.
export type OfferStatus = 'Draft' | 'NeedsCategory' | 'Pending' | 'Rejected' | 'Suspended' | 'Active' | 'Paused' | 'Scheduled' | 'Expired';

export interface OfferInput {
  title: string;
  // features.ts OFFER_CATEGORIES value; required to publish.
  category?: string | null;
  // Dining-bill offers: does the discount also apply to alcohol? Unanswered counts as yes.
  coversAlcohol?: boolean | null;
  // 'Percentage' (1-100), 'FixedAmount' (rupees) or 'Other'; required to publish.
  discountType?: string | null;
  discountValue?: number | null;
  // e.g. ['Fri', 'Sat']; empty = every day.
  activeDays?: string[];
  // The partner confirms the offer is accurate and lawful; needed to publish new or changed offers.
  confirmAccuracy?: boolean;
  offerType: string;
  benefit?: string | null;
  description: string;
  startDate?: string | null;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  maxClaims?: number | null;
  claimsPerUser: number;
  terms?: string | null;
  minimumSpend?: number | null;
  isPublished: boolean;
  isActive: boolean;
  isFeatured: boolean;
}

export interface OfferImage {
  id: number;
  url: string;
  isCover: boolean;
}

export interface Offer extends OfferInput {
  id: number;
  status: OfferStatus;
  claimsCount: number;
  redeemedCount: number;
  views: number;
  coverImageUrl?: string;
  images: OfferImage[];
  updatedAt: string;
  // The admin's reason when the offer was rejected or suspended.
  moderationNote?: string | null;
  // Why an Active offer still isn't shown to customers (venue not verified, category off, alcohol rules).
  hiddenReason?: string | null;
  partnerConfirmedAt?: string | null;
}

export interface OfferSaveResult {
  success: boolean;
  message: string;
  offer?: Offer;
}

export interface PublicOffer {
  id: number;
  title: string;
  offerType: string;
  benefit?: string;
  description: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  terms?: string;
  isFeatured: boolean;
  coverImageUrl?: string;
  minimumSpend?: number;
  category?: string;
  // When the venue last edited the offer: shown as "Updated by venue".
  lastUpdatedUtc?: string;
  discountType?: string | null;
  discountValue?: number | null;
  activeDays?: string[];
  startDate?: string;
}

export interface PartnerDashboard {
  activeOffers: number;
  totalClaims: number;
  redeemed: number;
  totalViews: number;
  claimsLast24h: number;
}

// Mobile is masked to its last 4 digits.
export interface PartnerClaim {
  id: number;
  offerTitle: string;
  mobile: string;
  claimedAtUtc: string;
  status: 'Claimed' | 'Confirmed' | 'Redeemed';
  redeemedAtUtc?: string;
}

export async function getPartnerClaims(): Promise<PartnerClaim[]> {
  const { data } = await api.get<PartnerClaim[]>('/api/account/claims');
  return data;
}

export async function getPartnerDashboard(): Promise<PartnerDashboard> {
  const { data } = await api.get<PartnerDashboard>('/api/account/dashboard');
  return data;
}

// Offer assistant (OfferAssistantEndpoints.cs): offer ideas from the venue's own POS sales.
export interface OfferDraft {
  title: string;
  category: string;
  discountType: string;
  discountValue: number;
  activeDays: string[];
  startTime?: string | null;
  endTime?: string | null;
  minimumSpend?: number | null;
  coversAlcohol?: boolean | null;
  offerType: string;
  description: string;
  terms?: string | null;
}

export interface OfferSuggestion { key: string; kind: 'SlowSlot' | 'Combo' | 'SlowItem' | 'BillBooster'; headline: string; reason: string; draft: OfferDraft }

export interface OfferInsights {
  from: string;
  to: string;
  orders: number;
  netSales: number;
  averageBill: number;
  busiestDay?: string | null;
  quietestDay?: string | null;
  topItems: { name: string; quantity: number; amount: number }[];
  suggestions: OfferSuggestion[];
  enoughData: boolean;
  ordersNeeded: number;
  aiWriting: boolean;
}

export async function getOfferInsights(days: number): Promise<OfferInsights> {
  const { data } = await api.get<OfferInsights>('/api/account/offer-assistant', { params: { days } });
  return data;
}

export async function writeOfferWording(draft: OfferDraft, reason: string): Promise<{ title: string; description: string }> {
  const { data } = await api.post<{ title: string; description: string }>('/api/account/offer-assistant/write', { draft, reason });
  return data;
}

export async function getMyOffers(): Promise<Offer[]> {
  const { data } = await api.get<Offer[]>('/api/account/offers');
  return data;
}

export async function getMyOffer(id: number): Promise<Offer> {
  const { data } = await api.get<Offer>(`/api/account/offers/${id}`);
  return data;
}

export async function createOffer(input: OfferInput): Promise<OfferSaveResult> {
  const { data } = await api.post<OfferSaveResult>('/api/account/offers', input);
  return data;
}

export async function saveOffer(id: number, input: OfferInput): Promise<OfferSaveResult> {
  const { data } = await api.put<OfferSaveResult>(`/api/account/offers/${id}`, input);
  return data;
}

export async function duplicateOffer(id: number): Promise<OfferSaveResult> {
  const { data } = await api.post<OfferSaveResult>(`/api/account/offers/${id}/duplicate`);
  return data;
}

export async function deleteOffer(id: number): Promise<void> {
  await api.delete(`/api/account/offers/${id}`);
}

export async function uploadOfferImage(offerId: number, file: File): Promise<OfferSaveResult> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post<OfferSaveResult>(`/api/account/offers/${offerId}/images`, form);
  return data;
}

export async function deleteOfferImage(offerId: number, imageId: number): Promise<OfferSaveResult> {
  const { data } = await api.delete<OfferSaveResult>(`/api/account/offers/${offerId}/images/${imageId}`);
  return data;
}

export async function setOfferCoverImage(offerId: number, imageId: number): Promise<OfferSaveResult> {
  const { data } = await api.post<OfferSaveResult>(`/api/account/offers/${offerId}/images/${imageId}/cover`);
  return data;
}

// imageIds is the whole gallery in its new order; the first becomes the cover.
export async function reorderOfferImages(offerId: number, imageIds: number[]): Promise<OfferSaveResult> {
  const { data } = await api.put<OfferSaveResult>(`/api/account/offers/${offerId}/images/order`, { imageIds });
  return data;
}

// Fire-and-forget: counts a view of a live offer's public page.
export function recordOfferView(offerId: number): void {
  api.post(`/api/discovery/offers/${offerId}/view`).catch(() => undefined);
}

export async function getVenueOffers(restoId: number): Promise<PublicOffer[]> {
  const { data } = await api.get<PublicOffer[]>(`/api/discovery/restaurants/${restoId}/offers`);
  return data;
}

export async function validateCustomer(mobile: string, promoCode: string): Promise<{ message: string }> {
  const { data } = await api.post('/api/account/validate-customer', { mobile, promoCode });
  return data;
}

export interface RestoDto {
  id: number;
  name: string;
  address: string;
  mobile1?: string;
  mobile2?: string;
  offer?: string;
  filePath?: string;
  distanceKm: number;
  offerImageUrl?: string;
  // Set when the headline offer is a partner-portal offer; claims pass it to bookDeal.
  offerId?: number;
  // How many live partner-portal offers the venue has (the headline one included).
  liveOfferCount?: number;
  // Venue location for the map and directions; 0 when unknown.
  latitude?: number;
  longitude?: number;
  state?: string | null;
  district?: string | null;
}

export interface ShopDto {
  id: number;
  name: string;
  address: string;
  mobile1?: string;
  mobile2?: string;
  filePath?: string;
  distanceKm: number;
  latitude?: number;
  longitude?: number;
  state?: string | null;
  district?: string | null;
}

// A live offer near the visitor. offerId is missing for a venue's legacy single offer.
export interface DiscoveryOffer {
  restoId: number;
  venueName: string;
  address: string;
  distanceKm: number;
  offerId?: number;
  title: string;
  offerType?: string;
  benefit?: string;
  description?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  isFeatured: boolean;
  imageUrl?: string;
  category?: string;
  lastUpdatedUtc?: string;
  discountType?: string | null;
  discountValue?: number | null;
  activeDays?: string[];
  venuePhone?: string | null;
  terms?: string | null;
  minimumSpend?: number | null;
}

// The State / District filter (hooks/useLocationFilter.ts). No state = "Near me" (within the radius).
export interface Area {
  state?: string | null;
  district?: string | null;
}

// Query params for an area; empty for "Near me".
export const areaParams = (area?: Area) =>
  area?.state ? { state: area.state, ...(area.district ? { district: area.district } : {}) } : {};

// A state that has venues, with the districts that have venues (the filter's dropdowns).
export interface LocationOption {
  state: string;
  districts: string[];
}

export const getLocations = async (): Promise<LocationOption[]> => (await api.get<LocationOption[]>('/api/discovery/locations')).data;

export async function getNearbyOffers(lat: number, lng: number, area?: Area): Promise<DiscoveryOffer[]> {
  const { data } = await api.get<DiscoveryOffer[]>('/api/discovery/offers', { params: { lat, lng, ...areaParams(area) } });
  return data;
}

export async function getRestaurants(lat: number, lng: number, area?: Area): Promise<RestoDto[]> {
  const { data } = await api.get<RestoDto[]>('/api/discovery/restaurants', { params: { lat, lng, ...areaParams(area) } });
  return data;
}

export async function getWineShops(lat: number, lng: number, area?: Area): Promise<ShopDto[]> {
  const { data } = await api.get<ShopDto[]>('/api/discovery/wineshops', { params: { lat, lng, ...areaParams(area) } });
  return data;
}

export const getRestaurant = async (id: number, lat: number, lng: number): Promise<RestoDto> =>
  (await api.get<RestoDto>(`/api/discovery/restaurants/${id}`, { params: { lat, lng } })).data;

export const getWineShop = async (id: number, lat: number, lng: number): Promise<ShopDto> =>
  (await api.get<ShopDto>(`/api/discovery/wineshops/${id}`, { params: { lat, lng } })).data;

// Indian states and union territories, as the API accepts them (c2n.api Locations.cs).
export const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir',
  'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
] as const;

export interface DealResult {
  success: boolean;
  message: string;
  dealId?: number;
}

export async function bookDeal(restoId: number, mobile: string, offerId?: number): Promise<DealResult> {
  const { data } = await api.post<DealResult>('/api/deals/book', { restoId, mobile, offerId });
  return data;
}

export async function confirmDeal(dealId: number, mobile: string, promoCode: string): Promise<DealResult> {
  const { data } = await api.post<DealResult>('/api/deals/confirm', { dealId, mobile, promoCode });
  return data;
}

export interface ContactRequest {
  firstName: string;
  lastName: string;
  city: string;
  email: string;
  phoneNumber: string;
  comments: string;
}

export async function saveContact(request: ContactRequest): Promise<void> {
  await api.post('/api/contact', request);
}

export interface SchedulerStatus {
  lastRunUtc?: string;
}

export interface SchedulerRunResult {
  deactivated: number;
  activated: number;
  updated: number;
  ranAtUtc: string;
}

export async function getSchedulerStatus(): Promise<SchedulerStatus> {
  const { data } = await api.get<SchedulerStatus>('/api/admin/scheduler/status');
  return data;
}

export async function runScheduler(): Promise<SchedulerRunResult> {
  const { data } = await api.post<SchedulerRunResult>('/api/admin/scheduler/run');
  return data;
}

export interface LogArchiveStatus {
  sourceExists: boolean;
  sourceSizeBytes: number;
  thresholdBytes: number;
  exceedsThreshold: boolean;
  backupExists: boolean;
  backupLastWriteUtc?: string;
}

export interface LogArchiveRunResult {
  archived: boolean;
  message: string;
}

export async function getLogArchiveStatus(): Promise<LogArchiveStatus> {
  const { data } = await api.get<LogArchiveStatus>('/api/admin/log-archive/status');
  return data;
}

export async function runLogArchive(force: boolean): Promise<LogArchiveRunResult> {
  const { data } = await api.post<LogArchiveRunResult>('/api/admin/log-archive/run', { force });
  return data;
}

export async function adminValidateCustomer(ownerEmail: string, mobile: string, promoCode: string): Promise<{ message: string }> {
  const { data } = await api.post('/api/admin/validate-customer', { ownerEmail, mobile, promoCode });
  return data;
}

export interface AdminVenue {
  restoId: number;
  name: string;
  address: string;
  mobile1?: string;
  ownerEmail?: string;
  offer?: string;
  filePath?: string;
}

export interface RegistrationCode {
  code: string;
  createdAt: string;
}

export async function getAdminVenues(): Promise<AdminVenue[]> {
  const { data } = await api.get<AdminVenue[]>('/api/admin/venues');
  return data;
}

// Admin console data tables. ownerEmail is missing for venues without an owner account.
export interface AdminPartner {
  restoId: number;
  name: string;
  address: string;
  mobile1?: string;
  mobile2?: string;
  latitude: number;
  longitude: number;
  ownerEmail?: string;
  hasLogin: boolean;
  liveOffers: number;
  totalOffers: number;
  totalClaims: number;
  redeemed: number;
  coverUrl?: string;
  state?: string | null;
  district?: string | null;
  // Liquor licence entered by the partner; alcohol-related offers need it verified and unexpired.
  liquorLicenceNumber?: string | null;
  liquorLicenceExpiry?: string | null;
  liquorLicenceVerifiedAt?: string | null;
  liquorLicenceVerifiedBy?: string | null;
  // Set when an admin has checked the venue's details; only verified venues' offers are shown.
  verifiedAt?: string | null;
  verifiedBy?: string | null;
}

export interface AdminPartnerInput {
  name: string;
  address: string;
  mobile1: string;
  mobile2?: string | null;
  latitude: number;
  longitude: number;
  // For the State / District filters; state must be one of INDIAN_STATES.
  state?: string | null;
  district?: string | null;
  ownerEmail?: string | null;
  // Create: empty = generate one (returned once). Update: empty = keep the current password.
  password?: string | null;
  // Create only: the application this approves (marked Approved; the owner is emailed their login).
  applicationId?: number | null;
}

export interface PartnerApplicationInput {
  businessName: string;
  ownerName: string;
  mobile: string;
  email: string;
  city: string;
  state?: string;
  pinCode?: string;
  businessType?: string;
  about?: string;
}

export interface PartnerApplication extends PartnerApplicationInput {
  id: number;
  status: 'Pending' | 'Approved' | 'Rejected';
  restoId?: number;
  createdAt: string;
  reviewedAt?: string;
}

export async function submitPartnerApplication(input: PartnerApplicationInput): Promise<void> {
  await api.post('/api/partner-applications', input);
}

export async function getPartnerApplications(): Promise<PartnerApplication[]> {
  const { data } = await api.get<PartnerApplication[]>('/api/admin/partner-applications');
  return data;
}

export async function rejectPartnerApplication(id: number): Promise<void> {
  await api.post(`/api/admin/partner-applications/${id}/reject`);
}

export interface AdminPartnerSaveResult {
  success: boolean;
  message: string;
  partner?: AdminPartner;
  generatedPassword?: string;
}

export interface AdminOfferRow {
  offerId: number;
  restoId: number;
  venueName: string;
  title: string;
  benefit?: string;
  status: OfferStatus;
  isFeatured: boolean;
  startDate?: string;
  endDate?: string;
  claims: number;
  redeemed: number;
  views: number;
  updatedAt: string;
  moderationStatus: 'Pending' | 'Approved' | 'Rejected' | 'Suspended';
  moderationNote?: string | null;
  description?: string | null;
  terms?: string | null;
  category?: string | null;
  isAlcoholRelated: boolean;
  venueLicenceValid: boolean;
  discountType?: string | null;
  discountValue?: number | null;
  activeDays?: string[];
  coversAlcohol?: boolean | null;
  partnerConfirmedAt?: string | null;
  partnerConfirmedBy?: string | null;
  venueVerified: boolean;
  hiddenReason?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  minimumSpend?: number | null;
}

// One version of an offer, and the review decisions, oldest first (admin console).
export interface OfferSnapshot {
  title: string;
  category?: string | null;
  coversAlcohol?: boolean | null;
  discountType?: string | null;
  discountValue?: number | null;
  offerType: string;
  benefit?: string | null;
  description: string;
  terms?: string | null;
  minimumSpend?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  activeDays?: string | null;
  isPublished: boolean;
  isActive: boolean;
  moderationStatus: string;
  moderationNote?: string | null;
  partnerConfirmedAt?: string | null;
  partnerConfirmedBy?: string | null;
}

export interface OfferRevision {
  id: number;
  createdAt: string;
  actor: string;
  action: string;
  note?: string | null;
  snapshot: OfferSnapshot;
}

export async function getOfferHistory(offerId: number): Promise<OfferRevision[]> {
  const { data } = await api.get<OfferRevision[]>(`/api/admin/offers/${offerId}/history`);
  return data;
}

export interface CategorySetting {
  category: string;
  enabled: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export async function getCategorySettings(): Promise<CategorySetting[]> {
  const { data } = await api.get<CategorySetting[]>('/api/admin/settings/categories');
  return data;
}

export async function saveCategorySetting(category: string, enabled: boolean, reason?: string): Promise<CategorySetting[]> {
  const { data } = await api.put<CategorySetting[]>(`/api/admin/settings/categories/${category}`, { enabled, reason });
  return data;
}

export async function verifyVenue(restoId: number, verified: boolean): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/api/admin/partners/${restoId}/verify`, { verified });
  return data;
}

// Every offer is approved or rejected (reject needs a reason) before customers see it; admins can
// also suspend one and reinstate it with "approve".
export async function moderateOffer(offerId: number, action: 'approve' | 'reject' | 'suspend', note?: string): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(`/api/admin/offers/${offerId}/moderate`, { action, note });
  return data;
}

// ---------- Promotion rules by country / state (Super Admin) ----------
// A state rule replaces its country's rule there; with no rule, offers are on and alcohol is off.

export interface PromotionRule {
  id: number;
  country: string;
  countryName: string;
  state?: string | null;
  offersEnabled: boolean;
  alcoholOffers: boolean;
  disabledCategories: string[];
  note?: string | null;
  venues: number;
  updatedAt: string;
  updatedBy?: string | null;
}

export interface PromotionRules { rules: PromotionRule[]; countries: Record<string, string>; indianStates: string[]; categories: string[] }

export interface PromotionRuleInput {
  country: string;
  state?: string | null;
  offersEnabled: boolean;
  alcoholOffers: boolean;
  disabledCategories: string[];
  note?: string | null;
  reason?: string;
}

export async function getPromotionRules(): Promise<PromotionRules> {
  const { data } = await api.get<PromotionRules>('/api/admin/promotion-rules');
  return data;
}

export async function savePromotionRule(rule: PromotionRuleInput): Promise<PromotionRules> {
  const { data } = await api.put<PromotionRules>('/api/admin/promotion-rules', rule);
  return data;
}

export async function deletePromotionRule(id: number): Promise<PromotionRules> {
  const { data } = await api.delete<PromotionRules>(`/api/admin/promotion-rules/${id}`);
  return data;
}

// ---------- Alcohol controls (Super Admin) and liquor licences ----------

export interface AlcoholSettings {
  submissionsEnabled: boolean;
  displayEnabled: boolean;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export async function getAlcoholSettings(): Promise<AlcoholSettings> {
  const { data } = await api.get<AlcoholSettings>('/api/admin/settings/alcohol');
  return data;
}

export async function saveAlcoholSettings(submissionsEnabled: boolean, displayEnabled: boolean, reason?: string): Promise<AlcoholSettings> {
  const { data } = await api.put<AlcoholSettings>('/api/admin/settings/alcohol', { submissionsEnabled, displayEnabled, reason });
  return data;
}

export interface VenueLicence {
  number?: string | null;
  expiry?: string | null;
  verifiedAt?: string | null;
  isValid: boolean;
  status: 'None' | 'Pending' | 'Verified' | 'Expired';
}

export async function getMyLicence(): Promise<VenueLicence> {
  const { data } = await api.get<VenueLicence>('/api/account/licence');
  return data;
}

export async function saveMyLicence(number: string | null, expiry: string | null): Promise<VenueLicence> {
  const { data } = await api.put<VenueLicence>('/api/account/licence', { number, expiry });
  return data;
}

export async function verifyVenueLicence(restoId: number, verified: boolean): Promise<VenueLicence> {
  const { data } = await api.post<VenueLicence>(`/api/admin/partners/${restoId}/licence`, { verified });
  return data;
}

export interface AuditLogEntry {
  id: number;
  createdAt: string;
  actorType: 'admin' | 'partner' | 'customer' | 'anonymous';
  actor: string;
  action: string;
  entityType: string;
  entityId?: string | null;
  details?: string | null;
  succeeded: boolean;
}

export async function getAuditLogs(take = 500): Promise<AuditLogEntry[]> {
  const { data } = await api.get<AuditLogEntry[]>('/api/admin/audit-logs', { params: { take } });
  return data;
}

export interface AdminClaimRow {
  id: number;
  restoId: number;
  venueName: string;
  offerTitle: string;
  mobile: string;
  claimedAtUtc: string;
  status: 'Claimed' | 'Confirmed' | 'Redeemed';
  redeemedAtUtc?: string;
}

export async function getAdminPartners(): Promise<AdminPartner[]> {
  const { data } = await api.get<AdminPartner[]>('/api/admin/partners');
  return data;
}

export async function createAdminPartner(input: AdminPartnerInput): Promise<AdminPartnerSaveResult> {
  const { data } = await api.post<AdminPartnerSaveResult>('/api/admin/partners', input);
  return data;
}

export async function updateAdminPartner(restoId: number, input: AdminPartnerInput): Promise<AdminPartnerSaveResult> {
  const { data } = await api.put<AdminPartnerSaveResult>(`/api/admin/partners/${restoId}`, input);
  return data;
}

export async function deleteAdminPartner(restoId: number): Promise<void> {
  await api.delete(`/api/admin/partners/${restoId}`);
}

export async function getAdminOffers(): Promise<AdminOfferRow[]> {
  const { data } = await api.get<AdminOfferRow[]>('/api/admin/offers');
  return data;
}

export async function getAdminClaims(): Promise<AdminClaimRow[]> {
  const { data } = await api.get<AdminClaimRow[]>('/api/admin/claims');
  return data;
}

export async function getRegistrationCodes(): Promise<RegistrationCode[]> {
  const { data } = await api.get<RegistrationCode[]>('/api/admin/registration-codes');
  return data;
}

export async function createRegistrationCode(): Promise<string> {
  const { data } = await api.post<{ code: string }>('/api/admin/registration-codes');
  return data.code;
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
  const { data } = await api.post('/api/account/change-password', { currentPassword, newPassword });
  return data;
}

// One session at a time: a partner/admin login (email + password) or a customer login (mobile + OTP).
// c2n_role tells them apart so customer sessions never unlock the partner portal or admin console.
export function saveSession(token: string, email: string, isAdmin: boolean) {
  clearSession();
  localStorage.setItem('c2n_token', token);
  localStorage.setItem('c2n_email', email);
  localStorage.setItem('c2n_is_admin', isAdmin ? '1' : '0');
  localStorage.setItem('c2n_role', isAdmin ? 'admin' : 'partner');
}

export interface CustomerSession {
  displayName: string;
  initials: string;
}

export function saveCustomerSession(token: string, customer: CustomerSession) {
  clearSession();
  localStorage.setItem('c2n_token', token);
  localStorage.setItem('c2n_role', 'customer');
  localStorage.setItem('c2n_customer', JSON.stringify(customer));
}

// A POS staff PIN login on a venue's tablet (only the POS, never the partner portal or admin).
export interface PosStaffSession {
  name: string;
  role: string;
  venueName: string;
}

export function savePosStaffSession(token: string, staff: PosStaffSession) {
  clearSession();
  localStorage.setItem('c2n_token', token);
  localStorage.setItem('c2n_role', 'pos-staff');
  localStorage.setItem('c2n_pos_staff', JSON.stringify(staff));
}

export function posStaffSession(): PosStaffSession | null {
  if (!localStorage.getItem('c2n_token') || localStorage.getItem('c2n_role') !== 'pos-staff') return null;
  try {
    return JSON.parse(localStorage.getItem('c2n_pos_staff') ?? 'null');
  } catch {
    return null;
  }
}

export function clearSession() {
  ['c2n_token', 'c2n_email', 'c2n_is_admin', 'c2n_role', 'c2n_customer', 'c2n_pos_staff'].forEach((k) => localStorage.removeItem(k));
}

// Partner or admin session (the portal / admin console). Customer and POS staff sessions don't count.
export function isLoggedIn(): boolean {
  const role = localStorage.getItem('c2n_role');
  return !!localStorage.getItem('c2n_token') && role !== 'customer' && role !== 'pos-staff';
}

export function isCustomer(): boolean {
  return !!localStorage.getItem('c2n_token') && localStorage.getItem('c2n_role') === 'customer';
}

export function customerSession(): CustomerSession | null {
  if (!isCustomer()) return null;
  try {
    return JSON.parse(localStorage.getItem('c2n_customer') ?? 'null');
  } catch {
    return null;
  }
}

// The partner/admin login email of the current session, if any.
export function sessionEmail(): string | null {
  return isLoggedIn() ? localStorage.getItem('c2n_email') : null;
}

export function isAdmin(): boolean {
  return localStorage.getItem('c2n_is_admin') === '1';
}
